'use client'

import dynamic from 'next/dynamic'
import { type FC } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

import {
  type OfferV11,
  OfferV1OfferTypeEnum,
  type PimProductV11,
  type PimProductV1Flags,
} from '@redteclab/api/clients/bully'

import { useDeviceDetectorContext } from '../../../device-detector'
import { ErrorBoundary } from '../../../error-boundary'
import { LoginRegistrationUserLoginStateProvider } from '../../../login-registration'
import { ProductVolumePricingContextProvider } from '../../../product'
import { productAccessorIsProductNotInCatalog } from '../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../model/productTemplates'
import { ProductPageNowDialog } from '../../now/components/ProductPageNowDialog/ProductPageNowDialog'
import { useHideStickyBannerWhenStickyBuyBoxIsVisible } from '../ProductBuyBox/ProductBuyBoxSticky/useHideStickyBannerWhenStickyBuyBoxIsVisible'
import { ProductHead } from '../ProductHead'
import { useProductPageContext } from '../productPageContext'

import { ProductOverviewMediaSection } from './ProductOverviewMediaSection'
import { ProductOverviewPurchaseSection } from './ProductOverviewPurchaseSection'

const ProductRecommendationWidgetFirstImpressionSlider = dynamic(async () =>
  import('../ProductRecommendationAreas/ProductRecommendationAreaFirstImpressionSlider').then(
    (mod) => mod.ProductRecommendationWidgetFirstImpressionSlider,
  ),
)

const ProductRecommendationWidgetOutOfStockSlider = dynamic(async () =>
  import('../ProductRecommendationAreas/ProductRecommendationAreaOutOfStockSlider').then(
    (mod) => mod.ProductRecommendationWidgetOutOfStockSlider,
  ),
)

const shouldRenderPrescriptionTypes = (
  currentOffer: OfferV11 | undefined,
  productTemplate: PRODUCT_TEMPLATES,
  flags: PimProductV1Flags | undefined,
): boolean => {
  if (flags?.veterinary.active) {
    return false
  }

  const isOtx = productTemplate === PRODUCT_TEMPLATES.OTX

  if (currentOffer) {
    return isOtx && currentOffer.offerType === OfferV1OfferTypeEnum.Sae
  }

  return isOtx
}

const canProductBeBought = (currentVariant: PimProductV11): boolean => {
  return (
    !productAccessorIsProductNotInCatalog(currentVariant.stock_status) &&
    !productAccessorIsProductOutOfStock(currentVariant.stock_status) &&
    !productAccessorIsProductOutOfSale(currentVariant.stock_status)
  )
}

const ProductOverview: FC<{
  BannerAndRecommendationBuyTogether?: FC
  ProductAvailableRecommendationWidget?: FC
}> = ({
  BannerAndRecommendationBuyTogether,
  ProductAvailableRecommendationWidget,
}) => {
  const { currentOffer, currentVariant } = useProductPageContext()
  const { isIntersecting, ref } = useIntersectionObserver({
    // Sticky Buy Box should be initially hidden as IntersectionObserver works on client-side only during scrolling down the page
    initialIsIntersecting: true,
  })
  const { isMobile } = useDeviceDetectorContext()

  const shouldShowStickyBuyBox =
    !isIntersecting && canProductBeBought(currentVariant)

  useHideStickyBannerWhenStickyBuyBoxIsVisible(shouldShowStickyBuyBox)

  return (
    <div ref={ref}>
      <ProductVolumePricingContextProvider
        currentVariantUpid={currentVariant.upid}
        volumeBasedPrices={currentOffer?.volumeBasedPrices ?? []}
      >
        <LoginRegistrationUserLoginStateProvider>
          <ProductPageNowDialog />
          <ErrorBoundary>
            <ProductRecommendationWidgetOutOfStockSlider
              className="mb-8 hidden tablet:block"
              itemsPerPage={6}
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <ProductRecommendationWidgetFirstImpressionSlider />
          </ErrorBoundary>
          <ProductHead />
          <div className="flex flex-col pb-7 tablet:flex-row">
            <ProductOverviewMediaSection />
            <ProductOverviewPurchaseSection
              bannerAndRecommendationBuyTogether={
                BannerAndRecommendationBuyTogether
              }
              isMobile={isMobile}
              productAvailableRecommendationWidget={
                ProductAvailableRecommendationWidget
              }
              shouldRenderPrescriptionTypes={shouldRenderPrescriptionTypes}
              shouldShowStickyBuyBox={shouldShowStickyBuyBox}
            />
          </div>
        </LoginRegistrationUserLoginStateProvider>
      </ProductVolumePricingContextProvider>
    </div>
  )
}

export { ProductOverview }
